// src/stores/auth.js
import { defineStore } from 'pinia';
import axios from 'axios';

const api = axios.create({
    baseURL: 'https://eisenbahn.wingen.de/api/user',
    headers: { 'Content-Type': 'application/json' },
});

let getToken = null;
export function bindTokenGetter(fn) { getToken = fn; }
let onUnauthorized = null;
export function bindUnauthorizedHandler(fn) { onUnauthorized = fn; }

api.interceptors.request.use((config) => {
    const token = getToken && getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});
api.interceptors.response.use(
    (res) => res,
    (err) => {
        const status  = err?.response?.status;
        const hadAuth = !!err?.config?.headers?.Authorization;
        if (status === 401 && hadAuth && onUnauthorized) {
            onUnauthorized();
        }
        return Promise.reject(err);
    }
);

const STORAGE_KEY = 'auth.v1';

export const useAuthStore = defineStore('auth', {
    state: () => ({
        token: null,
        tokenExpiresAt: null,
        email: null,
        firstName: null,
        lastName: null,
        loading: false,
        error: null,
        access: { checkedAt: null, active: null, grant: null },
        ui: {
            loginVisible: false,
            loginRedirect: null,
            loginTitle: 'Anmelden',
            prefillEmail: null,
            // Subscription Modal
            subscriptionVisible: false,
            // Favorites Modal
            favoritesVisible: false,
            favoritesImageId: null,
        },
    }),

    getters: {
        isAuthenticated: (s) => !!s.token,
        hasActiveAccess: (s) => s.access.active === true,
        accessUntil: (s) => (s.access.grant ? s.access.grant.ends_at : null),
    },

    actions: {
        viewLogin(opts) {
            this.ui.loginTitle = (opts && opts.title) || 'Anmelden';
            this.ui.loginRedirect = (opts && opts.redirect) || null;
            this.ui.prefillEmail = (opts && opts.email) || null;
            this.ui.loginVisible = true;
        },
        hideLogin() {
            this.ui.loginVisible = false;
            this.ui.loginRedirect = null;
            this.ui.prefillEmail = null;
        },

        // Subscription Modal
        viewSubscription() {
            this.ui.subscriptionVisible = true;
        },
        hideSubscription() {
            this.ui.subscriptionVisible = false;
        },

        // Favorites Modal
        viewFavorites(imageId) {
            this.ui.favoritesImageId = imageId;
            this.ui.favoritesVisible = true;
        },
        hideFavorites() {
            this.ui.favoritesVisible = false;
            this.ui.favoritesImageId = null;
        },

        loadFromStorage() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                if (!raw) return;
                const data = JSON.parse(raw);
                this.token = data.token || null;
                this.tokenExpiresAt = data.tokenExpiresAt || null;
                this.email = data.email || null;
                this.access = data.access || this.access;
            } catch {}
        },
        saveToStorage() {
            const payload = {
                token: this.token,
                tokenExpiresAt: this.tokenExpiresAt,
                email: this.email,
                access: this.access,
            };
            localStorage.setItem('auth.v1', JSON.stringify(payload));
        },

        normalizeError(err) {
            const apiMsg = err?.response?.data?.error;
            if (apiMsg) return apiMsg;
            if (err?.message) return err.message;
            return 'Unbekannter Fehler';
        },

        isTokenExpired() {
            if (!this.tokenExpiresAt) return true;
            return Date.now() >= Date.parse(this.tokenExpiresAt);
        },

        init({ validateOnStart = false } = {}) {
            this.loadFromStorage();
            bindTokenGetter(() => this.token);
            bindUnauthorizedHandler(() => { if (this.token) this.logout({ silentServer: true }); });

            if (validateOnStart && this.token) {
                this.fetchAccessMe().catch(() => {/* soft-fail, UI bleibt, Interceptor handled 401 */});
            }
            if (this.token && this.isTokenExpired()) {
                this.logout({ silentServer: true }); // Token ist clientseitig abgelaufen
            }
        },

        async checkEmail(email) {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.post('/check_email', { email });
                return { exists: data?.exists ?? false };
            } catch (err) {
                this.error = this.normalizeError(err);
                return { exists: false };
            } finally { this.loading = false; }
        },

        async register(email, password, firstName = null, lastName = null) {
            this.loading = true; this.error = null;
            try {
                await api.post('/register', {
                    email,
                    password,
                    first_name: firstName,
                    last_name: lastName
                });
                return true;
            } catch (err) {
                this.error = this.normalizeError(err);
                return false;
            } finally { this.loading = false; }
        },

        async login(email, password) {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.post('/login', { email, password });
                if (!data?.ok || !data?.token) throw new Error('Login fehlgeschlagen');

                this.token = data.token;
                this.tokenExpiresAt = data.expires_at || null;
                this.email = email;

                this.saveToStorage();

                // Optional: Header für den allerersten Call sofort setzen
                // (falls der Interceptor erst beim nächsten Request greift)
                api.defaults.headers.common.Authorization = `Bearer ${this.token}`;
                return true;
            } catch (err) {
                this.error = this.normalizeError(err);
                this.token = null;
                this.tokenExpiresAt = null;
                this.saveToStorage();
                return false;
            } finally {
                this.loading = false;
            }
        },

        async logout(opts = {}) {
            this.loading = true; this.error = null;
            try {
                if (!opts.silentServer) await api.post('/logout', {});
            } catch {}
            this.token = null;
            this.tokenExpiresAt = null;
            this.email = null;
            this.access = { checkedAt: null, active: null, grant: null };
            this.saveToStorage();
            this.loading = false;
        },

        async fetchAccessMe() {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.get('/access/me', {
                    headers: { Authorization: `Bearer ${this.token}` } // <-- wichtig beim ersten Call
                });
                this.access.checkedAt = new Date().toISOString();
                this.access.active = !!data?.active;
                this.access.grant = data?.grant || null;
                this.saveToStorage();
                return { active: this.access.active, grant: this.access.grant };
            } catch (err) {
                this.error = this.normalizeError(err);
                return { active: false, grant: null };
            } finally { this.loading = false; }
        },

        // PayPal Actions
        async createPayPalOrder(amount = 24) {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.post('/paypal/create-order', { amount }, {
                    headers: { Authorization: `Bearer ${this.token}` }
                });
                if (!data?.ok) throw new Error(data?.error || 'Could not create order');
                return { ok: true, orderId: data.order_id };
            } catch (err) {
                this.error = this.normalizeError(err);
                return { ok: false, error: this.error };
            } finally { this.loading = false; }
        },

        async capturePayPalOrder(orderId) {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.post('/paypal/capture-order', { order_id: orderId }, {
                    headers: { Authorization: `Bearer ${this.token}` }
                });
                if (!data?.ok) throw new Error(data?.error || 'Could not capture payment');

                // Update access state
                this.access.active = true;
                this.access.grant = data.access;
                this.access.checkedAt = new Date().toISOString();
                this.saveToStorage();

                return { ok: true, access: data.access };
            } catch (err) {
                this.error = this.normalizeError(err);
                return { ok: false, error: this.error };
            } finally { this.loading = false; }
        },

        // Change Password
        async changePassword(currentPassword, newPassword) {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.post('/change-password', {
                    current_password: currentPassword,
                    new_password: newPassword
                }, {
                    headers: { Authorization: `Bearer ${this.token}` }
                });
                if (!data?.ok) throw new Error(data?.error || 'Passwort konnte nicht geändert werden');
                return { ok: true };
            } catch (err) {
                this.error = this.normalizeError(err);
                return { ok: false, error: this.error };
            } finally { this.loading = false; }
        },

        // Delete Account
        async deleteAccount(password) {
            this.loading = true; this.error = null;
            try {
                const { data } = await api.post('/delete-account', {
                    password,
                    confirmed: true
                }, {
                    headers: { Authorization: `Bearer ${this.token}` }
                });
                if (!data?.ok) throw new Error(data?.error || 'Account konnte nicht gelöscht werden');

                // Clear local state
                this.token = null;
                this.tokenExpiresAt = null;
                this.email = null;
                this.access = { checkedAt: null, active: null, grant: null };
                this.saveToStorage();

                return { ok: true };
            } catch (err) {
                this.error = this.normalizeError(err);
                return { ok: false, error: this.error };
            } finally { this.loading = false; }
        },
    },
});
